/**
 * LifeReportGenerationPopup — KALKI 7.0 premium generation modal.
 *
 * Rendered inside `LifeReport.jsx` the instant the user clicks Generate.
 * The popup appears *synchronously*; the backend request is fired
 * optimistically after the first paint so the user always sees the UI
 * before any network latency.
 *
 * Behaviour (spec §2, §3, §9):
 *   • Opens with a 10:00 countdown bound to server timestamps.
 *   • Shows the current pipeline stage, taken verbatim from JobOut.stage
 *     — never a fake percentage.
 *   • "Stay & Watch"       → keeps modal open with full stages ladder.
 *   • "Generate in Background" → closes modal; `LifeReportResumeToast`
 *     portal (mounted globally in App.js) takes over.  Job keeps running.
 *   • After 10:00 the countdown is replaced with "Still preparing..."
 *     — the backend reaper remains the only authority on job success.
 *   • Terminal state COMPLETED → swaps to "✓ Your Life Report is ready"
 *     with "View Report" CTA.
 *   • Terminal state FAILED / REFUNDED → shows the error + "Retry"
 *     affordance; no credits were captured.
 */
import React, { useEffect, useMemo } from "react";
import useBodyScrollLock from "@/lib/useBodyScrollLock";
import { X, Check, Loader2 } from "lucide-react";

const INK   = "#1C1C1C";
const INDIGO = "#5D00FF";
const MUTE  = "#676773";
const SOFT  = "#F6F2FF";

function pad(n) { return String(n).padStart(2, "0"); }
function fmtTimer(sec) {
  if (sec == null || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${pad(m)}:${pad(s)}`;
}

const STAGE_ORDER = [
  "VALIDATING",
  "CREDITS_RESERVED",
  "CALCULATING_CHART",
  "ANALYZING_LIFE_EVENTS",
  "VEDIC_TIMING",
  "KALKI_WISDOM",
  "CALCULATING_PREDICTIONS",
  "GENERATING_REPORT",
  "RENDERING_PDF",
  "COMPLETED",
];

export default function LifeReportGenerationPopup({
  open,
  status,
  stageLabel,
  stagesCompleted,
  remainingSeconds,
  reportId,
  error,
  onStayAndWatch,       // no-op by default; here for API completeness
  onBackground,         // close modal (job keeps running via global toast)
  onViewReport,         // after COMPLETED
  onRetry,              // after FAILED/REFUNDED
  onClose,              // × button (same as background)
}) {
  // Lock body scroll while the modal is open (ref-counted so overlapping
  // modals never leak a permanent lock on Android).
  useBodyScrollLock(open);
  // Escape → treat as "generate in background" (job survives per spec §11).
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") (onBackground || onClose)?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onBackground, onClose]);

  const isCompleted = status === "COMPLETED";
  const isFailed    = status === "FAILED" || status === "REFUNDED";
  const isPreparing = !isCompleted && !isFailed;
  const timerText   = useMemo(() => fmtTimer(remainingSeconds), [remainingSeconds]);
  const overTime    = isPreparing && remainingSeconds <= 0;

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Life Report generation"
      data-testid="life-report-popup"
      style={{
        position: "fixed", inset: 0, zIndex: 1200,
        background: "rgba(10,8,30,0.6)",
        backdropFilter: "blur(14px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
      }}
    >
      <div className="kalki-snippet kalki-snippet--blessing" style={{
        width: "100%", maxWidth: 460,
        borderRadius: 24,
        boxShadow: "0 24px 80px rgba(10,8,30,0.35)",
        padding: 28, position: "relative",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        <button
          aria-label="Close"
          data-testid="life-report-popup-close"
          onClick={() => (onBackground || onClose)?.()}
          style={{
            position: "absolute", top: 18, right: 18,
            background: "transparent", border: "none",
            cursor: "pointer", color: MUTE, padding: 6,
          }}>
          <X size={18} />
        </button>

        <div style={{ fontSize: 11, letterSpacing: "0.3em",
                      textTransform: "uppercase", color: INDIGO,
                      marginBottom: 8 }}>
          KALKI · Vedic Life Report
        </div>

        {isPreparing && (
          <>
            <h2 style={{ fontSize: 22, fontWeight: 600, color: INK,
                         marginBottom: 6, lineHeight: 1.25 }}
                data-testid="life-report-popup-title">
              Preparing your Life Report
            </h2>
            <p style={{ fontSize: 13, color: MUTE, marginBottom: 20,
                        lineHeight: 1.55 }}>
              Your personalized Vedic Life Report is being prepared using
              your birth chart, life journey and Astro Monish calculations.
            </p>

            <div style={{
              background: SOFT, borderRadius: 16, padding: "20px 16px",
              textAlign: "center", marginBottom: 18,
            }}>
              {overTime ? (
                <div data-testid="life-report-popup-overtime">
                  <div style={{ fontSize: 15, color: INK, fontWeight: 500 }}>
                    Still preparing your report
                  </div>
                  <div style={{ fontSize: 12, color: MUTE, marginTop: 6 }}>
                    Taking a little longer than expected — generation is
                    continuing safely in the background.
                  </div>
                </div>
              ) : (
                <>
                  <div data-testid="life-report-popup-countdown"
                       style={{
                         fontSize: 44, fontWeight: 600, color: INK,
                         letterSpacing: "0.04em", fontVariantNumeric: "tabular-nums",
                       }}>
                    {timerText}
                  </div>
                  <div style={{ fontSize: 11, color: MUTE,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase", marginTop: 4 }}>
                    Estimated time
                  </div>
                </>
              )}
              <div style={{ display: "inline-flex", alignItems: "center",
                            gap: 8, marginTop: 14, fontSize: 12, color: INK }}
                   data-testid="life-report-popup-stage">
                <Loader2 size={14} style={{ animation: "spin 1.2s linear infinite" }} />
                {stageLabel || "Starting"}
              </div>
            </div>

            <StageLadder stagesCompleted={stagesCompleted} />

            <div style={{ fontSize: 12, color: MUTE, lineHeight: 1.55,
                          marginTop: 16 }}>
              Your report will continue generating even if you leave this
              page. Once ready, find it in <strong style={{ color: INK }}>
              Profile → My Life Reports</strong>.
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
              <button
                data-testid="life-report-popup-stay"
                onClick={() => onStayAndWatch?.()}
                style={btnStyle(true)}>
                Stay &amp; Watch
              </button>
              <button
                data-testid="life-report-popup-background"
                onClick={() => (onBackground || onClose)?.()}
                style={btnStyle(false)}>
                Generate in Background
              </button>
            </div>

            <div style={{ fontSize: 11, color: MUTE, marginTop: 14,
                          textAlign: "center" }}>
              <strong style={{ color: INK }}>500 credits reserved</strong>
              {" "}— charged only when your report is ready.
            </div>
          </>
        )}

        {isCompleted && (
          <>
            <h2 style={{ fontSize: 22, fontWeight: 600, color: INK,
                         marginBottom: 10 }}
                data-testid="life-report-popup-success-title">
              <Check size={22} style={{ color: "#15803D",
                                        verticalAlign: "middle",
                                        marginRight: 8 }} />
              Your Life Report is ready
            </h2>
            <p style={{ fontSize: 13, color: MUTE, marginBottom: 18 }}>
              Your personalized Vedic Life Report has been generated
              successfully. 500 credits have been applied.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                data-testid="life-report-popup-view"
                onClick={() => onViewReport?.(reportId)}
                style={btnStyle(true)}>
                View Report
              </button>
              <button
                data-testid="life-report-popup-dismiss"
                onClick={() => (onBackground || onClose)?.()}
                style={btnStyle(false)}>
                Later
              </button>
            </div>
          </>
        )}

        {isFailed && (
          <>
            <h2 style={{ fontSize: 22, fontWeight: 600, color: INK,
                         marginBottom: 10 }}
                data-testid="life-report-popup-failed-title">
              KALKI is recalibrating
            </h2>
            <p style={{ fontSize: 13, color: MUTE, marginBottom: 18 }}>
              {error || "Report generation didn't complete. "}
              <strong style={{ color: INK }}>No credits were charged.</strong>
              {" "}Please try again — your intake answers are preserved.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                data-testid="life-report-popup-retry"
                onClick={() => onRetry?.()}
                style={btnStyle(true)}>
                Retry Generation
              </button>
              <button
                data-testid="life-report-popup-close-failed"
                onClick={() => (onBackground || onClose)?.()}
                style={btnStyle(false)}>
                Close
              </button>
            </div>
          </>
        )}

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

function btnStyle(primary) {
  return {
    flex: 1, height: 44, borderRadius: 999,
    fontSize: 13, fontWeight: 500, cursor: "pointer",
    letterSpacing: "0.02em",
    background: primary ? INDIGO : "transparent",
    color: primary ? "white" : INK,
    border: primary ? "none" : `1px solid rgba(0,0,0,0.1)`,
    transition: "transform 160ms ease, background 160ms ease",
  };
}

function StageLadder({ stagesCompleted }) {
  const done = new Set(stagesCompleted || []);
  const visible = STAGE_ORDER.filter((s) => s !== "COMPLETED");
  const labelMap = {
    VALIDATING: "Understanding your birth details",
    CREDITS_RESERVED: "Reserving your credits",
    CALCULATING_CHART: "Calculating your Vedic birth chart",
    ANALYZING_LIFE_EVENTS: "Analyzing your life patterns",
    VEDIC_TIMING: "Vedic timing — dasha & transits",
    KALKI_WISDOM: "KALKI's wisdom · your interpretation",
    CALCULATING_PREDICTIONS: "Answering your questions",
    GENERATING_REPORT: "Preparing your Life Report",
    RENDERING_PDF: "Finalizing your PDF",
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}
         data-testid="life-report-popup-ladder">
      {visible.map((s) => (
        <div key={s} style={{
          display: "flex", alignItems: "center", gap: 10,
          fontSize: 11.5,
          color: done.has(s) ? INK : MUTE,
          opacity: done.has(s) ? 1 : 0.65,
        }} data-testid={`life-report-stage-${s.toLowerCase()}`}>
          <span style={{
            width: 14, height: 14, borderRadius: 999,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: done.has(s) ? "#15803D" : "rgba(0,0,0,0.08)",
            color: "white",
          }}>
            {done.has(s) ? <Check size={9} /> : null}
          </span>
          {labelMap[s] || s}
        </div>
      ))}
    </div>
  );
}
